import React, { useState } from 'react';
import { X, User, Upload, Camera } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    id: string;
    name: string;
    email: string;
    phone: string;
    photo_url: string | null;
  };
  participantNumber: number | null;
  onPhotoUpdate: (photoUrl: string) => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({
  isOpen,
  onClose,
  user,
  participantNumber,
  onPhotoUpdate
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setError(null);
      setUploading(true);

      if (!event.target.files || event.target.files.length === 0) {
        return;
      }

      const file = event.target.files[0];

      if (!file.type.startsWith('image/')) {
        setError('Per favore seleziona un file immagine');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('L\'immagine deve essere inferiore a 5MB');
        return;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `profile-photos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data: publicUrlData } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(filePath);

      const photoUrl = publicUrlData.publicUrl;

      const { error: updateError } = await supabase
        .from('contacts')
        .update({ photo_url: photoUrl })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      onPhotoUpdate(photoUrl);
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      setError(error.message || 'Errore nel caricamento della foto');
    } finally {
      setUploading(false);
    }
  };

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="sticky top-0 bg-gradient-to-r from-pink-500 to-rose-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <User className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Il Tuo Profilo</h2>
            </div>
            <button
              onClick={onClose}
              className="hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex flex-col items-center">
            <div className="relative mb-4">
              {user.photo_url ? (
                <img
                  src={user.photo_url}
                  alt="Profilo"
                  className="w-32 h-32 rounded-full object-cover border-4 border-pink-200"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white text-4xl font-bold border-4 border-pink-200">
                  {getInitials(user.email)}
                </div>
              )}
              <label
                htmlFor="photo-upload"
                className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-gray-50 transition-colors border-2 border-pink-200"
              >
                <Camera className="h-5 w-5 text-pink-600" />
              </label>
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
            </div>

            {uploading && (
              <p className="text-sm text-gray-600 mb-2">Caricamento in corso...</p>
            )}

            {error && (
              <div className="w-full p-3 bg-red-50 text-red-600 rounded-lg text-sm mb-4">
                {error}
              </div>
            )}

            {participantNumber !== null && (
              <div className="mb-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg p-4 border-2 border-pink-200 w-full">
                <p className="text-center text-gray-600 text-sm mb-1">Il tuo numero</p>
                <p className="text-center text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-rose-600">
                  #{participantNumber}
                </p>
              </div>
            )}

            <div className="w-full space-y-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Email</p>
                <p className="font-medium text-gray-900">{user.email}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Telefono</p>
                <p className="font-medium text-gray-900">{user.phone}</p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-pink-50 rounded-lg w-full">
              <div className="flex items-start gap-2">
                <Upload className="h-5 w-5 text-pink-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700">
                    Carica una foto profilo per essere facilmente riconoscibile dagli altri partecipanti.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Dimensione massima: 5MB
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
